import { useEffect, useRef, useState } from "react";

const DEFAULT_OPTS = {
  /* Root parent element. If null, will default to the window */
  root: null,
  rootMargin: "0px",
  /* 100% of element must be in view to be considered visible */
  threshold: 1,
};

const useIntersectionObserver = (
  optionProps: Partial<IntersectionObserverInit> = DEFAULT_OPTS
) => {
  const options = { ...DEFAULT_OPTS, ...optionProps };
  const targetElementRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const callbackFunction = (entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    setIsVisible(entry.isIntersecting);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callbackFunction, options);
    if (targetElementRef.current) observer.observe(targetElementRef.current);

    return () => {
      if (targetElementRef.current)
        observer.unobserve(targetElementRef.current);
    };
  }, [targetElementRef, options.root, options.rootMargin, options.threshold]);

  const scrollIntoView = (opts?: ScrollIntoViewOptions) => {
    if (!targetElementRef.current) return;
    targetElementRef.current.scrollIntoView(opts);
  };

  return { targetElementRef, isVisible, scrollIntoView };
};

export default useIntersectionObserver;
